import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  QrCode, Download, Loader2, PackageCheck, Globe,
  FileImage, Info, CheckCircle2
} from "lucide-react";

const AdminQRCodesPage = () => {
  const { t } = useTranslation('platform');
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);

  const fetchQRCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/qr-codes/list`, { withCredentials: true });
      setQrData(res.data);
    } catch (err) {
      console.error("Failed to fetch QR codes:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQRCodes(); }, [fetchQRCodes]);

  const handleDownload = async (qrId, filename) => {
    setDownloading(prev => ({ ...prev, [qrId]: true }));
    try {
      const res = await axios.get(`${API}/qr-codes/download/${qrId}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(prev => ({ ...prev, [qrId]: false }));
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const res = await axios.get(`${API}/qr-codes/download-all`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "charstan_production_qr_codes.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("ZIP download failed:", err);
    } finally {
      setDownloadingAll(false);
    }
  };

  if (loading) {
    return (
      <QMSLayout title={t('platform:qrCodes.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#00C8E0] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  const codes = qrData?.qr_codes || [];
  const spec = qrData?.spec || {};

  return (
    <QMSLayout title={t('platform:qrCodes.title')}>
      <div data-testid="admin-qr-codes-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 style={{ fontFamily: "'Syne', sans-serif" }}
                className="text-2xl font-bold text-white flex items-center gap-3">
              <QrCode className="w-7 h-7 text-[#00C8E0]" />
              Production QR Codes
            </h1>
            <p className="text-[#94A3B8] text-sm mt-1">
              READER-HUB-007 — Print-ready QR codes for hardcover book inserts
            </p>
          </div>
          <button
            data-testid="download-all-qr-zip"
            onClick={handleDownloadAll}
            disabled={downloadingAll}
            className="flex items-center gap-2 bg-[#1A6BCC] hover:bg-[#00C8E0] text-white px-5 py-2.5 rounded-[3px] font-medium text-sm tracking-wide uppercase transition-colors disabled:opacity-50"
          >
            {downloadingAll ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <PackageCheck className="w-4 h-4" />
            )}
            Download All (ZIP)
          </button>
        </div>

        {/* Spec Banner */}
        <div className="bg-[#132340] border border-[#1E3A5F] p-4 flex flex-wrap items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-[#00C8E0]">
            <Info className="w-4 h-4" />
            <span className="font-medium">Print Spec</span>
          </div>
          <div className="text-[#94A3B8]">
            <span className="text-white font-medium">{spec.dpi}</span> DPI
          </div>
          <div className="text-[#94A3B8]">
            Error Correction: <span className="text-white font-medium">{spec.error_correction}</span>
          </div>
          <div className="text-[#94A3B8]">
            Min Size: <span className="text-white font-medium">{spec.min_size_mm}</span> mm
          </div>
          <div className="text-[#94A3B8]">
            Quiet Zone: <span className="text-white font-medium">{spec.border_modules}</span> modules
          </div>
        </div>

        {/* QR Code Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {codes.map((qr) => (
            <div
              key={qr.id}
              data-testid={`qr-card-${qr.id}`}
              className="bg-[#0F1E35] border border-[#1E3A5F] overflow-hidden"
            >
              {/* Card Header */}
              <div className="border-b border-[#1E3A5F] px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#00C8E0]" />
                  <span className="text-white font-medium text-sm">{qr.label}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-[3px] uppercase tracking-wider font-bold ${
                    qr.lang === "en"
                      ? "bg-[#1A6BCC]/20 text-[#1A6BCC]"
                      : "bg-[#00E5C8]/20 text-[#00E5C8]"
                  }`}>
                    {qr.lang === "en" ? "EN" : "PT-BR"}
                  </span>
                </div>
                <CheckCircle2 className="w-4 h-4 text-[#00E5C8]" />
              </div>

              {/* QR Preview */}
              <div className="flex justify-center py-6 bg-white">
                <img
                  data-testid={`qr-preview-${qr.id}`}
                  src={`data:image/png;base64,${qr.preview_b64}`}
                  alt={`QR Code: ${qr.label}`}
                  className="w-48 h-48 object-contain"
                />
              </div>

              {/* Details */}
              <div className="px-5 py-4 space-y-3">
                <div>
                  <p className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1">Target URL</p>
                  <p className="text-[#00C8E0] text-sm font-mono break-all">{qr.url}</p>
                </div>
                <div>
                  <p className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1">Purpose</p>
                  <p className="text-white text-sm">{qr.purpose}</p>
                </div>
                <div className="flex items-center justify-between text-xs text-[#94A3B8]">
                  <span>
                    <FileImage className="w-3 h-3 inline mr-1" />
                    {qr.filename}
                  </span>
                  <span>{(qr.size_bytes / 1024).toFixed(1)} KB</span>
                </div>
              </div>

              {/* Download Button */}
              <div className="px-5 pb-5">
                <button
                  data-testid={`download-qr-${qr.id}`}
                  onClick={() => handleDownload(qr.id, qr.filename)}
                  disabled={downloading[qr.id]}
                  className="w-full flex items-center justify-center gap-2 bg-[#132340] border border-[#1E3A5F] hover:border-[#00C8E0] text-white px-4 py-2.5 rounded-[3px] font-medium text-sm tracking-wide uppercase transition-colors disabled:opacity-50"
                >
                  {downloading[qr.id] ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download PNG
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Print Instructions */}
        <div className="bg-[#132340] border border-[#1E3A5F] p-6 space-y-3">
          <h3 style={{ fontFamily: "'Syne', sans-serif" }}
              className="text-lg font-bold text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-[#00C8E0]" />
            Print Production Instructions
          </h3>
          <ol className="text-[#94A3B8] text-sm space-y-2 list-decimal list-inside">
            <li>Insert the downloaded PNGs into the hardcover <code className="text-[#00C8E0]">.docx</code> files at the designated QR placeholder locations.</li>
            <li>Ensure the printed size is at least <span className="text-white font-medium">30mm x 30mm</span>.</li>
            <li>After inserting, physically scan each QR code from the <span className="text-white font-medium">PDF proof</span> to confirm the landing page loads correctly.</li>
            <li>Do NOT resize below 30mm — scanner readability degrades at smaller sizes.</li>
            <li>Test with at least 3 different QR scanner apps to ensure compatibility.</li>
          </ol>
          <p className="text-[#00E5C8] text-xs font-medium mt-4">
            Do not send books to the printer until all QR codes have been verified by physical scan test.
          </p>
        </div>
      </div>
    </QMSLayout>
  );
};

export default AdminQRCodesPage;
